<script setup lang="ts">
import SurveyForm from './Components/SurveyForm.vue'
import type { SurveyQuestion } from '@/types/survey'
import axios from 'axios'

import { Loader2 } from 'lucide-vue-next'
import type { storeInterface } from '@/types/store'
import dayjs from 'dayjs'
const { t } = useI18n()
const router = useRouter()
const notification = useNotification()

const loaded = ref<boolean>(false)
const saving = ref<boolean>(false)
const isConfirmModalOpen = ref<boolean>(false)
const route = useRoute()

const surveyQuestion = ref<SurveyQuestion>({
  name: '',
  questionText: '',
  launchDate: null,
  endDate: null,
  isActive: true,
  choices: [
    {
      text: ''
    },
    {
      text: ''
    }
  ],
  stores: []
})

const isFromTemplate = computed(() => !surveyQuestion.value?.id && Boolean(route.query.templateId))

const pageTitle = computed(() => {
  return surveyQuestion.value?.id ? t('pages.survey.surveyUpdatePage.title') : t('pages.survey.surveyCreatePage.title')
})

const validate = () => {
  if (surveyQuestion.value.questionText === '') {
    notification.create('error', {
      text: t('pages.survey.surveyCreatePage.notifications.create.question')
    })
    return false
  }
  if (!surveyQuestion.value.launchDate) {
    notification.create('error', {
      text: t('pages.survey.surveyCreatePage.notifications.create.noDateSelected')
    })
    return false
  }
  if (surveyQuestion.value.endDate && surveyQuestion.value.launchDate > surveyQuestion.value.endDate) {
    notification.create('error', {
      text: t('pages.survey.surveyCreatePage.notifications.create.endDateBeforeLaunchDate')
    })
    return false
  }
  if (surveyQuestion.value.choices.filter((choice) => choice.text !== '').length < 2) {
    notification.create('error', {
      text: t('pages.survey.surveyCreatePage.notifications.create.answers.minimum')
    })
    return false
  }
  if (surveyQuestion.value.choices.filter((choice) => choice.text !== '').length > 5) {
    notification.create('error', {
      text: t('pages.survey.surveyCreatePage.notifications.create.answers.maximum')
    })
    return false
  }
  if (surveyQuestion.value.choices.filter((choice) => choice.text === '').length > 0) {
    notification.create('error', {
      text: t('pages.survey.surveyCreatePage.notifications.create.answers.notEmpty')
    })
    return false
  }
  if (surveyQuestion.value.stores.length === 0) {
    notification.create('error', {
      text: t('pages.survey.surveyCreatePage.notifications.create.stores')
    })
    return false
  }
  return true
}

const confirm = () => {
  if (!validate()) {
    return
  }

  if (surveyQuestion.value.id) {
    isConfirmModalOpen.value = true
  } else {
    save()
  }
}

const save = async () => {
  isConfirmModalOpen.value = false
  try {
    saving.value = true
    const params = {
      ...surveyQuestion.value,
      launchDate: surveyQuestion.value.launchDate ? dayjs(surveyQuestion.value.launchDate).format('YYYY-MM-DD') : null,
      endDate: surveyQuestion.value.endDate ? dayjs(surveyQuestion.value.endDate).format('YYYY-MM-DD') : null
    }
    if (surveyQuestion.value.id && confirm) {
      await axios.patch(`/surveys/${surveyQuestion.value.id}`, params)
    } else {
      await axios.post('/surveys', params)
    }
    notification.create('success', {
      text: surveyQuestion.value.id
        ? t('pages.survey.surveyUpdatePage.modal.creationComplete.text')
        : t('pages.survey.surveyCreatePage.modal.creationComplete.text')
    })
    router.push({ name: 'SurveyList' })
  } catch {
    notification.create('error', {
      text: surveyQuestion.value.id
        ? t('pages.survey.surveyUpdatePage.notifications.create.error')
        : t('pages.survey.surveyCreatePage.notifications.create.error')
    })
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  if (route.params.surveyId) {
    const { data } = await axios.get(`/surveys/${route.params.surveyId}`)
    surveyQuestion.value = data.data
    if (surveyQuestion.value.launchDate) {
      surveyQuestion.value.launchDate = dayjs.unix(data.data.launchDate).toDate()
    }
    if (surveyQuestion.value.endDate) {
      surveyQuestion.value.endDate = dayjs.unix(data.data.endDate).toDate()
    }
    surveyQuestion.value.stores = data.data.stores.map((store: storeInterface) => store.id)
  } else if (route.query.templateId) {
    const { data } = await axios.get(`/templates/${route.query.templateId}?module=survey-question`)
    surveyQuestion.value = { ...surveyQuestion.value, ...data.data.templatable }
  }
  loaded.value = true
})
</script>
<template>
  <div>
    <div v-if="loaded">
      <TheDetailHeader
        :title="pageTitle"
        :parent="{ label: t('pages.survey.surveyPage.title'), to: { name: 'SurveyList' } }"
      >
        <template v-if="isFromTemplate" #badge>
          <BaseChip color="yellow" :text="t('pages.survey.surveyCreatePage.labels.template')" />
        </template>
        <template #actions>
          <Button variant="default-primary" :disabled="saving" @click="confirm">
            <Loader2 v-if="saving" class="animate-spin" />
            {{
              surveyQuestion.id
                ? $t('pages.survey.surveyUpdatePage.buttons.save')
                : $t('pages.survey.surveyCreatePage.buttons.save')
            }}
          </Button>
        </template>
      </TheDetailHeader>
      <SurveyForm :survey-question="surveyQuestion" />
      <BaseModal
        :is-open="isConfirmModalOpen"
        icon="HeroExclamationTriangleIconSolid"
        icon-color="yellow"
        :link-text="$t('pages.survey.surveyUpdatePage.modal.confirm.linkText')"
        :text-title="$t('pages.survey.surveyUpdatePage.modal.confirm.title')"
        :cancel="$t('pages.survey.surveyUpdatePage.modal.confirm.cancel')"
        :text="$t('pages.survey.surveyUpdatePage.modal.confirm.text')"
        @close="isConfirmModalOpen = false"
        @click="save"
      />
    </div>
    <TheLoadingPage v-else />
  </div>
</template>
